import { ReportStatus, ReportTypeIdentifier, type ReportFilters } from '../../types/reports';
import { parseYearParam } from '../../utils/reportYearRange';

/**
 * URL-Suchparameter <-> `ReportFilters` fuer die Meldungenseite (D-05 bis D-09). Zwei reine
 * Funktionen ohne React-, Router- oder Mantine-Bezug, damit sie unter `environment: 'node'`
 * automatisiert pruefbar sind -- Vorbild: `periodFilterModel.ts` (Phase 22). Die Datei liegt
 * bewusst neben der Seite und nicht in `utils/`: sie kennt genau das URL-Schema DIESER Seite,
 * waehrend die Module aus Plan 02 seitenunabhaengig sind.
 *
 * Bewusst NICHT Teil dieses Moduls: das Exact-Match-Feld, das nach dieser Phase ausschliesslich
 * noch `usePreviousValuesLoader.ts` benutzt (siehe `types/reports.ts`) -- die Meldungenseite
 * setzt es seit Plan 25-04 nicht mehr, und der Bereich (`after`/`before`) ist ohnehin abgeleitet
 * und gehoert nicht in die URL.
 */

/** 1-basierter Periodenindex ohne fuehrende Null, `1`…`12`. */
const PERIOD_PATTERN = /^([1-9]|1[0-2])$/;

/**
 * Liest die fuenf URL-Parameter der Meldungenseite in `ReportFilters` ein.
 *
 * - `status`/`plant`/`type`: unveraendert aus dem Bestand -- geprueft gegen `ReportStatus`,
 *   `isNaN`, `ReportTypeIdentifier`.
 * - `year`: ueber `parseYearParam` (Plan 02). `URLSearchParams.get()` liefert bei fehlendem
 *   Schluessel `null` und bei `?year=` den leeren String -- genau daran haengt D-06/D-05, und
 *   `parseYearParam` unterscheidet die beiden Faelle bereits. NIEMALS
 *   `searchParams.get('year') ?? ''` davorschieben, das wuerde die Unterscheidung wieder tilgen.
 *   Der Schluessel wird nur gesetzt, wenn eine Zahl zurueckkommt, sonst gar nicht -- damit
 *   `toEqual({})` die Abwesenheit belegt und nicht ein explizites `undefined`.
 * - `period`: nur uebernommen, wenn der Rohwert dem Muster `1`…`12` ohne fuehrende Null
 *   entspricht (kein `parseInt` -- `parseInt('2026-03-01', 10)` liefert `2026` und wuerde das
 *   Altformat aus Lesezeichen vor dieser Phase stillschweigend in einen Periodenindex
 *   verwandeln) UND nur, wenn im rohen URL-Zustand ueberhaupt ein `year`-Schluessel vorhanden
 *   war (D-09) -- eine Periode ohne ein vom Nutzer gesetztes Jahr ergibt keinen Filter, selbst
 *   wenn `filters.year` am Ende durch die Vorbelegung trotzdem einen Wert traegt.
 */
export function parseUrlFilters(searchParams: URLSearchParams): ReportFilters {
  const filters: ReportFilters = {};

  // D-18: wiederholter Schluessel (`?status=draft&status=submitted`); ein alter Link mit nur
  // einem `status` liefert eine Liste mit einem Element und bleibt damit gueltig.
  const statuses = [...new Set(searchParams.getAll('status'))].filter((value): value is ReportStatus =>
    Object.values(ReportStatus).includes(value as ReportStatus)
  );
  if (statuses.length > 0) {
    filters.status = statuses;
  }

  const plant = searchParams.get('plant');
  if (plant) {
    const plantId = parseInt(plant, 10);
    if (!isNaN(plantId)) {
      filters.plant = plantId;
    }
  }

  const reportType = searchParams.get('type');
  if (reportType && Object.values(ReportTypeIdentifier).includes(reportType as ReportTypeIdentifier)) {
    filters.reportType = reportType as ReportTypeIdentifier;
  }

  const rawYear = searchParams.get('year');
  const year = parseYearParam(rawYear);
  if (year !== undefined) {
    filters.year = year;
  }

  const rawPeriod = searchParams.get('period');
  if (rawYear !== null && rawPeriod && PERIOD_PATTERN.test(rawPeriod)) {
    filters.period = rawPeriod;
  }

  return filters;
}

/**
 * Kehrt `parseUrlFilters` um: `ReportFilters` -> `URLSearchParams`. Gibt die Parameter zurueck,
 * statt sie selbst zu setzen -- nur so ist die Funktion ohne Router pruefbar, der Aufrufer
 * uebergibt das Ergebnis an `setSearchParams`.
 */
export function filtersToSearchParams(filters: ReportFilters): URLSearchParams {
  const params = new URLSearchParams();

  if (Array.isArray(filters.status)) {
    for (const status of filters.status) {
      params.append('status', status);
    }
  } else if (typeof filters.status === 'string') {
    params.set('status', filters.status);
  }
  if (filters.plant) {
    params.set('plant', String(filters.plant));
  }
  if (filters.reportType) {
    params.set('type', filters.reportType);
  }
  // D-05/D-06: der leere Wert ist Absicht, kein Schoenheitsfehler -- ohne ihn fehlt der
  // Schluessel komplett, und ein Reload deutet das als "kein Parameter" und reaktiviert die
  // Vorbelegung auf das laufende Jahr. Der Nutzer bekaeme sein bewusst geleertes Jahr zurueck.
  if (typeof filters.year === 'number') {
    params.set('year', String(filters.year));
  } else {
    params.set('year', '');
  }
  if (filters.period) {
    params.set('period', filters.period);
  }

  return params;
}

/**
 * D-19: Schluessel des gemerkten Filterstands je Benutzer (mehrere Personen koennen denselben
 * Browser nutzen). Der Speicherzugriff selbst liegt in der Seite, nicht hier.
 */
export function reportsFilterStorageKey(userId: string): string {
  return `reports.filter.${userId}`;
}

/**
 * D-19: Ausgangsparameter der Meldungenseite. Ein Link mit eigenen Parametern gewinnt; ohne
 * Parameter (Aufruf ueber das Menue) gilt der gespeicherte Stand. Der gespeicherte Stand traegt
 * durch `filtersToSearchParams` immer einen `year`-Schluessel -- deshalb bleibt "alle Jahre"
 * erhalten, und die Jahres-Vorbelegung (D-05/D-06) greift nur ohne Speicherstand (Pitfall 12).
 * Ein gespeichertes aelteres Jahr bleibt bewusst stehen. Robust gegen kaputte Werte: die
 * Ergebnisse laufen anschliessend durch `parseUrlFilters`, das jeden Wert validiert.
 */
export function resolveInitialSearchParams(
  current: URLSearchParams,
  stored: string | null
): URLSearchParams {
  if ([...current.keys()].length > 0) {
    return current;
  }
  try {
    return new URLSearchParams(stored ?? '');
  } catch {
    return new URLSearchParams();
  }
}
